import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import { translateNormalizedRole } from "@utils/roles";
import { checkPermission } from "@utils/user-permissions";
import { useTranslation } from "react-i18next";
import AttachmentsComponent from "../../../components/AttachmentComponent/AttachmentsComponent";
import { formatDateTime } from "../../utils";
import { LANG_BASE, TEST_ID_BASE } from "../DepartureStatus/DepartureStatus";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  rejectionReason?: string;
  rejectionDocument?: string;
  rejectionDocumentName?: string;
  internalNotes?: string;
  internalDocument?: string;
  internalDocumentName?: string;
  externalNotes?: string;
  externalDocument?: string;
  externalDocumentName?: string;
  noDisputeDocument?: string;
  noDisputeDocumentName?: string;
  submittedBy?: string;
  submittedByRole?: string;
  submittedAt?: string;
  isDispute?: boolean;
};

const service = "bff-vessel-call";

const DetailsSideDrawer = ({
  isOpen,
  onClose,
  rejectionReason,
  rejectionDocument,
  rejectionDocumentName,
  internalNotes,
  internalDocument,
  internalDocumentName,
  externalNotes,
  externalDocument,
  externalDocumentName,
  noDisputeDocument,
  noDisputeDocumentName,
  submittedBy,
  submittedByRole,
  submittedAt,
  isDispute = false,
}: Props) => {
  const { t } = useTranslation();
  const { userAccessPermissions } = useAuthStore();
  const isMarineDepartment = checkPermission(userAccessPermissions, "hasTaskAccess");
  const { handleDownload, handlePreview } = useDownloadCertificate();
  const submittedByName = translateNormalizedRole(t, submittedBy);
  const submittedByRoleText = translateNormalizedRole(t, submittedByRole);
  const hasSubmittedByInfo = Boolean(submittedByName || submittedByRoleText);
  const submittedAtText = submittedAt ? formatDateTime(submittedAt) : "-";

  return (
    <div data-testid="departure-details-sidedrawer" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onClose}
        keepMounted={false}
        title={t(`${LANG_BASE}.details-title`)}
        submitLabel={t("common.ok")}
        hideCancelButton={true}
      >
        <div className="flex flex-col gap-4 w-[400px] p-6">
          {rejectionReason && (
            <div className="col-span-full flex flex-col gap-2">
              <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
                {isDispute ? t(`${LANG_BASE}.dispute-reason`) : t(`${LANG_BASE}.rejection-reason`)}
              </CustomFormLabel>
              <div
                className="text-sm whitespace-pre-wrap wrap-break-word"
                data-testid={`${TEST_ID_BASE}-static-content`}
              >
                {rejectionReason}
              </div>
            </div>
          )}
          {rejectionDocument && (
            <AttachmentsComponent
              data-testid={`${TEST_ID_BASE}-external-attachment`}
              documentName={rejectionDocumentName ?? ""}
              onDownload={() => {
                handleDownload("document", service, rejectionDocument, rejectionDocumentName);
              }}
              onPreview={() => handlePreview("document", service, rejectionDocument)}
              langBase={LANG_BASE}
            />
          )}
          {noDisputeDocument && (
            <AttachmentsComponent
              data-testid={`${TEST_ID_BASE}-non-dispute-attachment`}
              documentName={noDisputeDocumentName ?? ""}
              onDownload={() => {
                handleDownload("document", service, noDisputeDocument, noDisputeDocumentName);
              }}
              onPreview={() => handlePreview("document", service, noDisputeDocument)}
              langBase={LANG_BASE}
            />
          )}
          {externalNotes && (
            <div className="col-span-full flex flex-col gap-2">
              <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
                {t(`${LANG_BASE}.external-notes`)}
              </CustomFormLabel>
              <div
                className="text-sm whitespace-pre-wrap wrap-break-word"
                data-testid={`${TEST_ID_BASE}-external-notes`}
              >
                {externalNotes}
              </div>
            </div>
          )}
          {externalDocument && (
            <AttachmentsComponent
              data-testid={`${TEST_ID_BASE}-external-attachment`}
              documentName={externalDocumentName ?? ""}
              onDownload={() => {
                handleDownload("document", service, externalDocument, externalDocumentName);
              }}
              onPreview={() => handlePreview("document", service, externalDocument)}
              langBase={LANG_BASE}
            />
          )}
          {internalNotes && isMarineDepartment && (
            <div className="col-span-full flex flex-col gap-2">
              <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
                {t(`${LANG_BASE}.internal-notes`)}
              </CustomFormLabel>
              <div
                className="text-sm whitespace-pre-wrap wrap-break-word"
                data-testid={`${TEST_ID_BASE}-internal-notes`}
              >
                {internalNotes}
              </div>
            </div>
          )}
          {internalDocument && isMarineDepartment && (
            <AttachmentsComponent
              data-testid={`${TEST_ID_BASE}-internal-attachment`}
              documentName={internalDocumentName ?? ""}
              onDownload={() => {
                handleDownload("document", service, internalDocument, internalDocumentName);
              }}
              onPreview={() => handlePreview("document", service, internalDocument)}
              langBase={LANG_BASE}
            />
          )}
          <div className="text-xs text-gray-500">
            <div>
              <span>{t(`${LANG_BASE}.submitted-by`)}</span>
              <span>
                {hasSubmittedByInfo ? (
                  <>
                    {submittedByName && <span className="text-gray-800">{submittedByName}</span>}
                    {submittedByName && submittedByRoleText ? " / " : null}
                    {submittedByRoleText}
                  </>
                ) : (
                  "-"
                )}
              </span>
            </div>
            <div>
              <span>{submittedAtText}</span>
            </div>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default DetailsSideDrawer;
